/**
 * Stub: expo-file-system (and expo-file-system/legacy)
 *
 * On web/desktop there is no device filesystem. All FileSystem calls are
 * no-ops that return safe defaults so callers don't crash.
 * Real file operations (voice notes, TTS, document scanning, etc.) are
 * native-only features that are gated or replaced by web alternatives.
 */

const noop = () => Promise.resolve(null);

// ── Constants ──────────────────────────────────────────────────────────────────
export const cacheDirectory = null;
export const documentDirectory = null;
export const bundleDirectory = null;

// ── Enums ──────────────────────────────────────────────────────────────────────
export const EncodingType = {
  UTF8: 'utf8',
  Base64: 'base64',
};

export const FileSystemUploadType = {
  BINARY_CONTENT: 0,
  MULTIPART: 1,
};

export const FileSystemSessionType = {
  BACKGROUND: 0,
  FOREGROUND: 1,
};

// ── Core file operations ───────────────────────────────────────────────────────
// ED-2400: readAsStringAsync must handle blob: URIs emitted by the
// expo-speech-recognition web stub so VoiceRecorder can read recorded audio
// on web. All other URIs (file://, null, etc.) remain no-ops.
export const readAsStringAsync = (uri, _options = {}) => {
  if (!uri || typeof uri !== 'string') return Promise.resolve('');
  if (uri.startsWith('blob:')) {
    return fetch(uri)
      .then((r) => r.arrayBuffer())
      .then((buf) => {
        const bytes = new Uint8Array(buf);
        let binary = '';
        // Process in 8 KB chunks to avoid call-stack overflow on large recordings.
        const CHUNK = 8192;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
        }
        return btoa(binary);
      })
      .catch(() => '');
  }
  return Promise.resolve('');
};
export const writeAsStringAsync    = noop;
export const deleteAsync           = noop;
export const moveAsync             = noop;
export const copyAsync             = noop;
export const makeDirectoryAsync    = noop;
export const readDirectoryAsync    = () => Promise.resolve([]);

// ── Info ───────────────────────────────────────────────────────────────────────
export const getInfoAsync = () =>
  Promise.resolve({ exists: false, isDirectory: false, uri: '', size: 0, modificationTime: 0 });

// ── Download / Upload ──────────────────────────────────────────────────────────
export const downloadAsync = () =>
  Promise.resolve({ uri: '', status: 0, headers: {}, md5: undefined });

export const uploadAsync = () =>
  Promise.resolve({ status: 0, headers: {}, body: '' });

export const createDownloadResumable = () => ({
  downloadAsync: () => Promise.resolve({ uri: '', status: 0, headers: {} }),
  pauseAsync: noop,
  resumeAsync: () => Promise.resolve({ uri: '', status: 0, headers: {} }),
  savable: () => ({ url: '', fileUri: '', options: {}, resumeData: '' }),
});

// ── Asset / Blob ───────────────────────────────────────────────────────────────
export const getContentUriAsync = () => Promise.resolve('');

// ── Class-style APIs (native only; stubs prevent import errors) ────────────────
export class File {}
export const Paths = {
  cache: null,
  document: null,
  bundle: null,
  main: null,
  join: (...parts) => parts.filter(Boolean).join('/'),
};

export default {
  cacheDirectory,
  documentDirectory,
  bundleDirectory,
  EncodingType,
  FileSystemUploadType,
  FileSystemSessionType,
  readAsStringAsync,
  writeAsStringAsync,
  deleteAsync,
  moveAsync,
  copyAsync,
  makeDirectoryAsync,
  readDirectoryAsync,
  getInfoAsync,
  downloadAsync,
  uploadAsync,
  createDownloadResumable,
  getContentUriAsync,
  File,
  Paths,
};
